import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  Box,
  Button,
  FormGroup,
  Input,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { AccountCircle, VpnKeyRounded } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { submitLogin } from '../../../auth/store/loginSlice';

const schema = yup
  .object({
    username: yup
      .string()
      .email('You must provide valid email address')
      .required('Email is required'),
    password: yup
      .string()
      .min(8, 'Password must be at least 8 symbols long')
      .max(32, 'Password must be less than 32 symbols long')
      .required('Password is required'),
  })
  .required();

function LoginForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });
  const { error, isLoading: loginInProgress } = useSelector(({ auth }) => auth.login);
  const onSubmit = (data) => dispatch(submitLogin(data));

  function handleEnter(e) {
    if (e.key === 'Enter') {
      handleSubmit(onSubmit)();
    }
  }

  return (
    <Box className="m-auto w-2/3 max-w-md">
      <Typography variant="h5" component="h1" className="text-center mb-16">
        Log in to your account
      </Typography>
      <FormGroup>
        <div className="mb-16">
          <Input
            className="w-full"
            variant="outlined"
            color="success"
            placeholder="Email"
            {...(errors.username?.message && { error: true })}
            {...register('username')}
            startAdornment={
              <InputAdornment position="start">
                <AccountCircle />
              </InputAdornment>
            }
          />
          {errors.username?.message && <ErrorMessage>{errors.username?.message}</ErrorMessage>}
        </div>
        <div className="mb-16">
          <Input
            className="w-full"
            variant="outlined"
            color="success"
            placeholder="Password"
            type="password"
            onKeyUp={handleEnter}
            {...(errors.password?.message && { error: true })}
            {...register('password')}
            startAdornment={
              <InputAdornment position="start">
                <VpnKeyRounded />
              </InputAdornment>
            }
          />
          {errors.password?.message && <ErrorMessage>{errors.password?.message}</ErrorMessage>}
        </div>

        {Boolean(error) && (
          <>
            <ErrorMessage>{error}</ErrorMessage>
            <br />
          </>
        )}

        <Box className="flex flex-col vtwo:gap-4">
          <Button
            className="w-full"
            variant="contained"
            type="submit"
            onClick={handleSubmit(onSubmit)}
            disabled={loginInProgress}
          >
            {loginInProgress && <CircularProgress size={20} className="vtwo:mr-2.5" />}Sign In
          </Button>
          <Button className="w-full" onClick={() => navigate('/forgot-password')}>
            Forgot Password
          </Button>
        </Box>
      </FormGroup>
    </Box>
  );
}

export default LoginForm;

const ErrorMessage = styled.div`
  color: red;
`;
